import { useMemo, memo, useCallback } from "react";
import { type StyleProp, type ViewStyle } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { getTimezoneFromAlarmDetail } from "@src/features/alarms/utils/alarm-utils";
import { findNextTransitionOptimized } from "@src/features/alarms/utils/alarm-actions";
import { dateTimeRecordToLocalisedDateTime } from "@visioni/schedules";
import { getCameraKeyString, formatCameraLabel } from "@src/utils/key-parser";
import type { CameraApiResponse } from "@src/features/cameras";
import { useCameraArmedStatus } from "@src/features/cameras/hooks/useCameraArmedStatus";
import { Icon, IconNameType } from "@src/components/Icon";
import { toSentenceCase } from "@visioni/a3s-ui/utils";
import { SnapshotCard } from "@visioni/a3s-ui/components";
import type { Href } from "expo-router";
import { NavPressable } from "@src/components/NavPressable";
import { SignedSnapshot, SnapshotImage } from "@visioni/a3s-media/app";
import { LegacySnapshotImage } from "./LegacySnapshotImage";
import { isWeb } from "@src/utils/web-helpers";

/**
 * Height for the detail slot, derived from the content layout.
 * Native: padding (10×2) + name (24) + subtitle (4+16) + divider (12+1+12) + badge (20) + schedule (16) = 125px
 * Web: padding (18×2) + name (24) + subtitle (4+16) + divider (12+1+12) + badge (20) + schedule (16) = 141px
 */
const DETAIL_HEIGHT = isWeb ? 141 : 125;

export interface CameraListItemProps {
  camera: CameraApiResponse;
  snapshot?: SignedSnapshot | null;
  siteId: string;
  onSelect: (cameraKey: string) => void;
  href?: Href;
  /** When true, applies selected state styling (e.g. for side panel context) */
  isSelected?: boolean;
  /** Optional style merged into the outer pressable container (e.g. flex:1 for grid cells). */
  style?: StyleProp<ViewStyle>;
}

export const CameraListItem = memo(
  ({ camera, siteId, onSelect, href, isSelected = false, snapshot, style }: CameraListItemProps) => {
    const { theme } = useUnistyles();
    const snapshotHeight = Math.ceil(theme.spacing["15xl"] * 1.465);
    const cameraKey = getCameraKeyString(camera);
    const { alarmDetail, isArmed } = useCameraArmedStatus({ camera, siteId });

    const isPaused = false; // TODO: API pending

    const scheduleTime = useMemo(() => {
      if (!alarmDetail) return null;
      const nextTransition = findNextTransitionOptimized({ alarmDetail });
      if (!nextTransition) return null;

      const timezone = getTimezoneFromAlarmDetail(alarmDetail) || "UTC";
      const transitionDateTime = dateTimeRecordToLocalisedDateTime(nextTransition.datetimeRecord, timezone);

      const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
      const day = dayNames[transitionDateTime.weekday - 1];

      const hours = transitionDateTime.hour;
      const minutes = transitionDateTime.minute;
      const ampm = hours >= 12 ? "PM" : "AM";
      const formattedHours = hours % 12 || 12;
      const formattedMinutes = minutes.toString().padStart(2, "0");

      const action = nextTransition.toState === "armed" ? "Arms" : "Disarms";
      return `${action} ${day} ${formattedHours}:${formattedMinutes} ${ampm}`;
    }, [alarmDetail]);

    const displayName = camera.name || formatCameraLabel(cameraKey);
    const subtitleName = camera.name ? formatCameraLabel(cameraKey) : null;

    const statusBadge = useMemo(() => {
      if (isPaused) {
        return {
          label: "Paused",
          iconName: "pause" as IconNameType,
          backgroundColor: theme.colors.grey[500],
          borderColor: theme.colors.grey[400],
        };
      }
      if (isArmed) {
        return {
          label: "Armed",
          iconName: "lockClosed" as IconNameType,
          backgroundColor: theme.colors.purple[600],
          borderColor: theme.colors.purple[300],
        };
      }
      return {
        label: "Disarmed",
        iconName: "lockOpen" as IconNameType,
        backgroundColor: theme.colors.warning[700],
        borderColor: theme.colors.warning[400],
      };
    }, [isPaused, isArmed, theme]);

    const handlePress = useCallback(() => onSelect(cameraKey), [onSelect, cameraKey]);

    const renderSnapshot = useCallback(
      () =>
        snapshot ?
          <SnapshotImage
            snapshot={snapshot}
            width={400}
            style={styles.thumbnail}
            placeholderColor={theme.colors.borderCard}
          />
        : <LegacySnapshotImage
            cameraKey={cameraKey}
            thumbnailUrl={camera.snapshot?.url}
            capturedAt={camera.snapshot?.capturedAt}
          />,
      [snapshot, cameraKey, camera.snapshot?.url, camera.snapshot?.capturedAt, theme.colors.borderCard],
    );

    const renderDetail = useCallback(
      () => (
        <View p={isWeb ? "4xl" : "lg"} style={styles.containerCameraInfo}>
          <Text textStyle="md/medium" numberOfLines={1} color="text">
            {toSentenceCase(displayName)}
          </Text>
          {subtitleName && (
            <Text textStyle="sm/regular" mt="xs" numberOfLines={1} style={styles.textSecondary}>
              {subtitleName}
            </Text>
          )}

          <View style={styles.bottomBlock}>
            <View borderWidth={1} borderColor="muted" my={isWeb ? "xl" : "lg"} />

            <View
              flexDirection={isWeb ? "row" : "column"}
              alignItems={isWeb ? "center" : "flex-start"}
              justifyContent={isWeb ? "space-between" : "flex-start"}
              gap="xs"
              style={styles.containerCameraStatus}
            >
              {/* Armed/Disarmed Status */}
              <View
                style={[
                  styles.statusBadge,
                  { backgroundColor: statusBadge.backgroundColor, borderColor: statusBadge.borderColor },
                ]}
              >
                <Icon name={statusBadge.iconName} size={14} color={theme.colors.white.default} />
                <Text textStyle="xs/regular" style={styles.textBadge}>
                  {statusBadge.label}
                </Text>
              </View>
              <View>
                <Text textStyle="xs/regular" style={styles.textSecondary}>
                  {scheduleTime ?? "No upcoming changes"}
                </Text>
              </View>
            </View>
          </View>
        </View>
      ),
      [displayName, subtitleName, statusBadge, scheduleTime, theme],
    );

    const selectedStyle: ViewStyle | undefined =
      isSelected ?
        { backgroundColor: theme.colors.surfaceCardInner, borderColor: theme.colors.borderCard, borderWidth: 1 }
      : undefined;

    if (href) {
      return (
        <NavPressable onPress={handlePress} href={href} style={[selectedStyle, style]}>
          <SnapshotCard
            renderSnapshot={renderSnapshot}
            renderDetail={renderDetail}
            detailHeight={DETAIL_HEIGHT}
            snapshotHeight={snapshotHeight}
          />
        </NavPressable>
      );
    }

    return (
      <SnapshotCard
        renderSnapshot={renderSnapshot}
        renderDetail={renderDetail}
        detailHeight={DETAIL_HEIGHT}
        snapshotHeight={snapshotHeight}
        onPress={handlePress}
        accessibilityLabel={toSentenceCase(displayName)}
        style={[selectedStyle, style]}
      />
    );
  },
  (prevProps, nextProps) => {
    const cameraKeyEqual =
      prevProps.camera.key.id === nextProps.camera.key.id && prevProps.camera.key.asset === nextProps.camera.key.asset;
    const snapshotEqual = prevProps.snapshot?.url === nextProps.snapshot?.url;
    const legacySnapshotEqual =
      prevProps.camera.snapshot?.url === nextProps.camera.snapshot?.url &&
      prevProps.camera.snapshot?.capturedAt === nextProps.camera.snapshot?.capturedAt;
    const siteIdEqual = prevProps.siteId === nextProps.siteId;
    // Compare alarmSettings to ensure re-render when alarm state changes
    const alarmSettingsEqual =
      JSON.stringify(prevProps.camera.alarmSettings) === JSON.stringify(nextProps.camera.alarmSettings);
    const isSelectedEqual = prevProps.isSelected === nextProps.isSelected;
    const hrefEqual = prevProps.href === nextProps.href;
    const styleEqual = prevProps.style === nextProps.style;

    return (
      cameraKeyEqual &&
      snapshotEqual &&
      legacySnapshotEqual &&
      siteIdEqual &&
      alarmSettingsEqual &&
      isSelectedEqual &&
      hrefEqual &&
      styleEqual
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  thumbnail: {
    width: "100%",
    height: "100%",
  },
  containerCameraInfo: {
    flex: 1,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderBottomLeftRadius: theme.spacing.md,
    borderBottomRightRadius: theme.spacing.md,
  },
  bottomBlock: {
    marginTop: "auto",
  },
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.xs,
    paddingHorizontal: theme.spacing.xs,
    paddingVertical: theme.spacing.xxs,
    borderRadius: theme.spacing["3xl"],
    borderWidth: 1,
  },
  containerCameraStatus: {
    overflow: "hidden",
  },
  textBadge: {
    color: theme.colors.white.default,
  },
  textSecondary: {
    color: theme.colors.surface1.foregroundSecondary,
  },
}));
